import { useEffect, useState } from 'react'
import { CalendarDays, ExternalLink, Inbox, MapPin, Trash2, TriangleAlert } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { cardImagePosition, formatDateRange, formatPlaces, formatWhen } from '../lib/format'
import type { ImportedDraft } from '../types'
import { LoadingState } from './LoadingState'

type Props = {
  canEdit: boolean
  onOpenDraft: (id: string) => void
  onNotice: (message: string) => void
  onError: (message: string) => void
}

// Where an imported event happens and when, from its sessions still to come.
function summary(draft: ImportedDraft) {
  const runs = draft.snapshot.runs.filter((run) => run.status !== 'deleted')
  const names = runs.map((run) => run.place_name.trim()).filter(Boolean)
  const days = runs.flatMap((run) => run.occurrences.map((item) => item.starts_at.slice(0, 10))).sort()
  return { place: formatPlaces([...new Set(names)]), dates: formatDateRange(days[0], days.at(-1)) }
}

const hostOf = (link: string) => {
  try { return new URL(link).hostname.replace(/^www\./, '') } catch { return link }
}

// The events Claude imported from a link (PRD.md, item 93), as a queue: a
// person checks each one in the editor before it reaches the app, as Google
// Maps does with suggested edits.
export function ReviewPage({ canEdit, onOpenDraft, onNotice, onError }: Props) {
  const [items, setItems] = useState<ImportedDraft[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      setItems((await cmsApi.listReview()).items)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar os eventos para revisar.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  // Discarded and kept: the same event never comes back from that page.
  const discard = async (draft: ImportedDraft) => {
    if (!window.confirm(`Descartar “${draft.title || 'Sem título'}”? Ele não volta numa nova importação.`)) return
    try {
      await cmsApi.discardDraft(draft.id)
      setItems((current) => current.filter((item) => item.id !== draft.id))
      onNotice('Evento descartado.')
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível descartar o evento.')
    }
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>Para revisar</h1>
          <p>Eventos importados de um link pelo Claude. Abra cada um, confira com a página de origem e publique ou descarte.</p>
        </div>
      </section>

      <section className="panel panel--flush" aria-label="Eventos para revisar">
        {loading ? <LoadingState label="Carregando…" /> : error ? (
          <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={() => void load()}>Tentar novamente</button></div>
        ) : items.length ? (
          <div className="review-list">
            {items.map((draft) => {
              const { place, dates } = summary(draft)
              const cover = draft.snapshot.event.image_url || draft.snapshot.event.image_url_source
              return (
                <article className="review-item" key={draft.id} aria-label={draft.title || 'Sem título'}>
                  <button className="review-item__main" onClick={() => onOpenDraft(draft.id)}>
                    <span className="review-item__image">{cover ? <img src={cover} alt="" style={{ objectPosition: cardImagePosition(draft.snapshot.event) }} /> : <CalendarDays size={22} />}</span>
                    <span className="review-item__content">
                      <strong>{draft.title || 'Sem título'}</strong>
                      <span><MapPin size={14} /> {place}</span>
                      <span><CalendarDays size={14} /> {dates}</span>
                      {draft.warnings.length > 0 && (
                        <ul className="review-item__warnings" aria-label="Avisos">
                          {draft.warnings.map((warning) => <li key={warning}><TriangleAlert size={14} /> {warning}</li>)}
                        </ul>
                      )}
                    </span>
                  </button>
                  <div className="review-item__side">
                    <small>{draft.updated_by_email === 'Importação' ? `Importado · ${formatWhen(draft.created_at)}` : `Editado por ${draft.updated_by_email}`}</small>
                    {draft.source_url
                      ? <a className="text-button" href={draft.source_url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> {hostOf(draft.source_url)}</a>
                      : <small>De um print</small>}
                    {canEdit && <button className="icon-button icon-button--danger" aria-label={`Descartar ${draft.title || 'Sem título'}`} title="Descartar" onClick={() => void discard(draft)}><Trash2 size={17} /></button>}
                  </div>
                </article>
              )
            })}
          </div>
        ) : (
          <div className="empty-state"><Inbox size={30} /><h3>Nada para revisar</h3><p>Mande links de eventos ao Claude com /importar e eles aparecem aqui.</p></div>
        )}
      </section>
    </div>
  )
}
